import React, {useEffect, useState} from 'react';
import {concatenate} from "services/string";
import {SortAscendingIcon, SortDescendingIcon, SwitchVerticalIcon} from "@heroicons/react/solid";
import {SortDirection} from 'types/SortDirection';

enum Alignment {LEFT, CENTER, RIGHT}

enum DisplaySize {ALL, X_SMALL, SMALL, MEDIUM, LARGE, X_LARGE}

type Props = {
    alignment: Alignment
    displaySize: DisplaySize
    children?: React.ReactNode
    sortable?: boolean
    sortDirection?: SortDirection
    onSort?: (direction: SortDirection) => any
}

const alignmentClasses: Record<Alignment, string> = {
    [Alignment.LEFT]: 'text-left',
    [Alignment.CENTER]: 'text-center',
    [Alignment.RIGHT]: 'text-right',
}

const displaySizeClasses: Record<DisplaySize, string> = {
    [DisplaySize.ALL]: '',
    [DisplaySize.X_SMALL]: 'hidden xs:table-cell',
    [DisplaySize.SMALL]: 'hidden sm:table-cell',
    [DisplaySize.MEDIUM]: 'hidden md:table-cell',
    [DisplaySize.LARGE]: 'hidden lg:table-cell',
    [DisplaySize.X_LARGE]: 'hidden xl:table-cell',
}

function TableHeader({alignment, displaySize, sortable, sortDirection, onSort, children}: Props) {
    const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection>(sortDirection ?? SortDirection.NONE);

    useEffect(() => {
        if(sortDirection !== undefined) setCurrentSortDirection(sortDirection)
    }, [sortDirection]);

    const cycleSortDirection = () => {
        let nextSortDirection: SortDirection

        switch(sortDirection){
            case SortDirection.NONE:
                nextSortDirection = SortDirection.ASCENDING
                break
            case SortDirection.ASCENDING:
                nextSortDirection = SortDirection.DESCENDING
                break
            default:
                nextSortDirection = SortDirection.NONE
        }

        setCurrentSortDirection(nextSortDirection)
        onSort && onSort(nextSortDirection)
    }

    return (
        <th scope="col" className={concatenate(alignmentClasses[alignment], displaySizeClasses[displaySize], 'px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider')}>
            {!sortable &&
                <span>{children}</span>
            }

            {sortable &&
                <button onClick={cycleSortDirection} className="flex space-x-1 items-center uppercase tracking-wider text-xs font-medium">
                    <span>{children}</span>

                    {currentSortDirection === SortDirection.NONE &&
                        <SwitchVerticalIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    }

                    {currentSortDirection === SortDirection.ASCENDING &&
                        <SortAscendingIcon className="h-4 w-4 flex-shrink-0" />
                    }

                    {currentSortDirection === SortDirection.DESCENDING &&
                        <SortDescendingIcon className="h-4 w-4 flex-shrink-0" />
                    }
                </button>
            }
        </th>
    );
}

TableHeader.alignments = Alignment
TableHeader.displaySizes = DisplaySize

TableHeader.defaultProps = {
    alignment: Alignment.LEFT,
    displaySize: DisplaySize.ALL
}

export default TableHeader;